/**
 * Browser session auth for the embeddable reservation widget.
 *
 * The widget cannot hold x-api-key. It mints a short-lived token via
 * POST /Session/issueBrowser (Origin/Referer validated server-side against
 * the brand) and attaches it as x-ibe-session-token on IBE API calls.
 */

import { getIBE_API } from './getAPIUrl';

export const SESSION_TOKEN_HEADER = 'x-ibe-session-token';

type CachedToken = {
  token: string;
  refreshAfter: number;
  brandId: string;
  chainId: string;
};

const REFRESH_MARGIN_MS = 60_000;

let cached: CachedToken | null = null;
let inFlight: Promise<string | null> | null = null;

async function mintSessionToken(brandId: string, chainId: string): Promise<string | null> {
  if (!brandId || !chainId) return null;

  const api = getIBE_API().replace(/\/$/, '');
  try {
    const response = await fetch(`${api}/Session/issueBrowser`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ BrandID: brandId, ChainID: chainId }),
      cache: 'no-store',
    });

    if (!response.ok) {
      console.error('widget session token: mint failed', response.status);
      return null;
    }

    const data = await response.json().catch(() => null);
    const token = data?.body?.token;
    if (!data?.status || !token) return null;

    const lifetimeMs = Math.max(0, (data.body.expiresInSeconds || 0) * 1000);
    cached = {
      token,
      brandId,
      chainId,
      refreshAfter: Date.now() + Math.max(0, lifetimeMs - REFRESH_MARGIN_MS),
    };
    return token;
  } catch (error) {
    console.error('widget session token: unexpected error', error);
    return null;
  }
}

export async function getWidgetSessionToken(
  brandId: string,
  chainId: string,
  forceRefresh = false
): Promise<string | null> {
  if (
    !forceRefresh &&
    cached &&
    cached.brandId === brandId &&
    cached.chainId === chainId &&
    Date.now() < cached.refreshAfter
  ) {
    return cached.token;
  }

  if (forceRefresh) cached = null;

  if (!inFlight) {
    inFlight = mintSessionToken(brandId, chainId).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** fetch() that attaches x-ibe-session-token for IBE API calls. */
export async function ibeAuthenticatedFetch(
  brandId: string,
  chainId: string,
  input: string,
  init: RequestInit = {}
): Promise<Response> {
  const token = await getWidgetSessionToken(brandId, chainId);
  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set(SESSION_TOKEN_HEADER, token);
  }
  if (brandId) headers.set('x-ibe-brand-id', brandId);
  if (chainId) headers.set('x-ibe-chain-id', chainId);

  const response = await fetch(input, { ...init, headers });
  if (response.status !== 401) return response;

  const refreshed = await getWidgetSessionToken(brandId, chainId, true);
  if (!refreshed) return response;

  headers.set(SESSION_TOKEN_HEADER, refreshed);
  return fetch(input, { ...init, headers });
}

/** Alias used by SearchControl / nationalityHelper (url, init, brandId, chainId). */
export async function ibeFetch(
  input: string,
  init: RequestInit = {},
  brandId?: string,
  chainId?: string
): Promise<Response> {
  if (brandId && chainId) {
    return ibeAuthenticatedFetch(brandId, chainId, input, init);
  }
  return fetch(input, init);
}

/** Alias for callers that expect getSessionToken(brandId, chainId). */
export const getSessionToken = getWidgetSessionToken;
